import { action, computed, makeObservable, observable } from "mobx";
import clsx from "clsx";
import { ViewModel } from "@orderofchaos/mvvm";
import type { Day, Month } from "@/types";
import type { Locale } from "./text.i18n";
import styles from "./PickADayInput.module.css";
import type { PickADayInputProps } from "./PickADayInput";

const MOBILE_PICKER_MEDIA_QUERY = "(max-width: 768px)";

interface PickADayDateValue {
  day: Day;
  month: Month;
  year: number;
}

function formatIsoDate(value: PickADayDateValue): string {
  const year = String(value.year).padStart(4, "0");
  const month = String(value.month).padStart(2, "0");
  const day = String(value.day).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoDate(value: string): PickADayDateValue | null {
  const raw = value.trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const jsDate = new Date(year, month - 1, day);
  if (
    Number.isNaN(jsDate.getTime()) ||
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  ) {
    return null;
  }

  return {
    day: day as Day,
    month: month as Month,
    year,
  };
}

function getTodayDate(): PickADayDateValue {
  const today = new Date();
  return {
    day: today.getDate() as Day,
    month: (today.getMonth() + 1) as Month,
    year: today.getFullYear(),
  };
}

export class PickADayInputViewModel extends ViewModel<
  unknown,
  PickADayInputProps
> {
  @observable
  isOpen = false;

  private rootElement: HTMLDivElement | null = null;

  private isPointerDownAttached = false;

  private isKeyDownAttached = false;

  private isScrollLocked = false;

  private previousBodyOverflow = "";

  private readonly pointerDownHandler = (event: PointerEvent): void => {
    if (!this.isOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    if (this.rootElement?.contains(target)) {
      return;
    }

    this.closePicker();
  };

  private readonly keyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.closePicker();
    }
  };

  constructor() {
    super();
    makeObservable(this);
  }

  protected onViewMounted(): void {
    this.syncEffects();
  }

  protected onViewUpdated(): void {
    this.syncEffects();
  }

  protected onViewUnmounted(): void {
    this.detachPointerDownListener();
    this.detachKeyDownListener();
    this.unlockBodyScroll();
  }

  @computed
  get rootClassName(): string {
    return clsx(styles.PickADayInput, this.viewProps.className);
  }

  @computed
  get value(): string {
    return this.viewProps.value;
  }

  @computed
  get placeholder(): string {
    return this.viewProps.placeholder || "ГГГГ-ММ-ДД";
  }

  @computed
  get disabled(): boolean {
    return this.viewProps.disabled ?? false;
  }

  @computed
  get locale(): Locale {
    return this.viewProps.locale || "ru";
  }

  @computed
  get flip(): boolean {
    return this.viewProps.flip ?? false;
  }

  @computed
  get pickerDate(): PickADayDateValue {
    return parseIsoDate(this.value) || getTodayDate();
  }

  @action.bound
  setRootElement(element: HTMLDivElement | null): void {
    this.rootElement = element;
  }

  @action.bound
  openPicker(): void {
    if (this.disabled) {
      return;
    }

    this.isOpen = true;
  }

  @action.bound
  closePicker(): void {
    this.isOpen = false;
  }

  @action.bound
  togglePicker(): void {
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;
  }

  @action.bound
  onDatePicked(nextDate: PickADayDateValue): void {
    this.viewProps.onChangeValue(formatIsoDate(nextDate));
    this.isOpen = false;
  }

  private syncEffects(): void {
    if (this.disabled && this.isOpen) {
      this.isOpen = false;
    }

    if (!this.isOpen) {
      this.detachPointerDownListener();
      this.detachKeyDownListener();
      this.unlockBodyScroll();
      return;
    }

    this.attachPointerDownListener();
    this.attachKeyDownListener();

    if (this.isMobileViewport()) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  private isMobileViewport(): boolean {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }

    return window.matchMedia(MOBILE_PICKER_MEDIA_QUERY).matches;
  }

  private attachPointerDownListener(): void {
    if (typeof document === "undefined" || this.isPointerDownAttached) {
      return;
    }

    document.addEventListener("pointerdown", this.pointerDownHandler, true);
    this.isPointerDownAttached = true;
  }

  private detachPointerDownListener(): void {
    if (typeof document === "undefined" || !this.isPointerDownAttached) {
      return;
    }

    document.removeEventListener("pointerdown", this.pointerDownHandler, true);
    this.isPointerDownAttached = false;
  }

  private attachKeyDownListener(): void {
    if (typeof document === "undefined" || this.isKeyDownAttached) {
      return;
    }

    document.addEventListener("keydown", this.keyDownHandler);
    this.isKeyDownAttached = true;
  }

  private detachKeyDownListener(): void {
    if (typeof document === "undefined" || !this.isKeyDownAttached) {
      return;
    }

    document.removeEventListener("keydown", this.keyDownHandler);
    this.isKeyDownAttached = false;
  }

  private lockBodyScroll(): void {
    if (typeof document === "undefined" || this.isScrollLocked) {
      return;
    }

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.isScrollLocked = true;
  }

  private unlockBodyScroll(): void {
    if (typeof document === "undefined" || !this.isScrollLocked) {
      return;
    }

    document.body.style.overflow = this.previousBodyOverflow;
    this.isScrollLocked = false;
  }
}
